<script setup lang="ts">
/**
 * The top bar's search, ⌘K / Ctrl+K: results appear as you type, the
 * screens of the menu, outlets and contacts, each opened by a click or by
 * the arrows and Enter. Enter with nothing chosen opens the full contact
 * search, as before.
 */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useQuery } from '@tanstack/vue-query'
import { api } from '@/lib/api'
import { can, hasModule, screenOn } from '@/lib/auth'
import { useDebounced } from '@/lib/debounce'
import { matches } from '@/lib/search'
import Icon from '@/ui/Icon.vue'
import type { IconName } from '@/ui/icons'
import { visibleNav } from '../nav'

interface Hit { key: string; kind: 'page' | 'outlet' | 'contact'; icon: IconName; label: string; detail: string; to: string | { path: string; query: Record<string, string> } }

const { t } = useI18n()
const router = useRouter()
const text = ref('')
const input = ref<HTMLInputElement | null>(null)
const open = ref(false)
const active = ref(-1)
const q = useDebounced(() => text.value.trim(), 250)
const long = computed(() => q.value.length >= 2)

const outlets = useQuery({
  queryKey: computed(() => ['quick-outlets', q.value]),
  enabled: computed(() => long.value && hasModule('sales')),
  queryFn: () => api<{ outlets: Array<{ id: string; name: string; code: string | null; city: string | null; phone1: string | null }> }>(
    `/outlets?${new URLSearchParams({ q: q.value, limit: '5' })}`),
})
const contacts = useQuery({
  queryKey: computed(() => ['quick-contacts', q.value]),
  enabled: long,
  queryFn: () => api<{ contacts: Array<{ id: string; firstName: string | null; lastName: string | null; company: string | null
                                          phone1: string | null; listName: string }> }>(
    `/contacts?${new URLSearchParams({ q: q.value, limit: '5' })}`),
})

const hits = computed<Hit[]>(() => {
  if (!q.value) return []
  const pages: Hit[] = visibleNav(can, screenOn).flatMap((g) => g.items)
    .filter((i) => matches(t(`nav.${i.key}.label`), q.value)).slice(0, 4)
    .map((i) => ({ key: `p${i.key}`, kind: 'page', icon: i.icon, label: t(`nav.${i.key}.label`), detail: t('shell.search.page'), to: i.to }))
  const shops: Hit[] = (long.value ? outlets.data.value?.outlets ?? [] : []).map((o) => ({
    key: `o${o.id}`, kind: 'outlet', icon: 'store', label: o.name,
    detail: [o.code, o.city, o.phone1].filter(Boolean).join(' · '), to: { path: '/outlets', query: { open: o.id } },
  }))
  const people: Hit[] = (long.value ? contacts.data.value?.contacts ?? [] : []).map((c) => ({
    key: `c${c.id}`, kind: 'contact', icon: 'user',
    label: [c.firstName, c.lastName].filter(Boolean).join(' ') || c.company || c.phone1 || '—',
    detail: [c.company, c.phone1, c.listName].filter(Boolean).join(' · '),
    to: { path: '/contacts', query: { q: q.value, contact: c.id } },
  }))
  return [...pages, ...shops, ...people]
})
const groups = computed(() => (['page', 'outlet', 'contact'] as const)
  .map((kind) => ({ kind, items: hits.value.filter((h) => h.kind === kind) })).filter((g) => g.items.length))
const loading = computed(() => long.value && (outlets.isFetching.value || contacts.isFetching.value))
// Nothing chosen until the arrows choose: Enter alone keeps opening the full contact search.
// Results arriving later (outlets, contacts) keep the line chosen, where it now is.
watch(hits, (now, before) => {
  const key = before?.[active.value]?.key
  active.value = key ? now.findIndex((h) => h.key === key) : -1
})

function go(h: Hit) {
  open.value = false
  text.value = ''
  input.value?.blur()
  void router.push(h.to)
}
function submit() {
  const chosen = hits.value[active.value]
  if (chosen && open.value) return go(chosen)
  const typed = text.value.trim()
  if (typed) { open.value = false; void router.push({ path: '/contacts', query: { q: typed } }) }
}
function onInputKey(e: KeyboardEvent) {
  if (!hits.value.length) return
  if (e.key === 'ArrowDown') { e.preventDefault(); open.value = true; active.value = (active.value + 1) % hits.value.length }
  if (e.key === 'ArrowUp') { e.preventDefault(); active.value = (active.value - 1 + hits.value.length) % hits.value.length }
  if (e.key === 'Escape') { open.value = false }
}
function onKey(e: KeyboardEvent) {
  // The physical key too: with an Arabic layout, the K key types « ن ».
  if ((e.metaKey || e.ctrlKey) && (e.key.toLowerCase() === 'k' || e.code === 'KeyK')) {
    e.preventDefault()
    input.value?.focus()
    open.value = true
  }
}
onMounted(() => window.addEventListener('keydown', onKey))
onUnmounted(() => window.removeEventListener('keydown', onKey))
// A click elsewhere closes the results (after the click on a result has run).
const blur = () => setTimeout(() => { open.value = false }, 150)
</script>

<template>
  <form role="search" class="relative min-w-0 max-w-[380px] flex-1" @submit.prevent="submit">
    <Icon name="search" class="pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
    <input ref="input" v-model="text" type="search" dir="auto" autocomplete="off" :placeholder="t('shell.search.placeholder')"
           :aria-label="t('shell.search.label')" role="combobox" :aria-expanded="open && Boolean(text)" aria-controls="quick-search-results"
           :aria-activedescendant="open && hits[active] ? `qs-${hits[active]!.key}` : undefined"
           class="h-9 w-full rounded-input border border-input bg-muted/50 pe-12 ps-8 text-[13px] focus:border-ring focus:bg-card focus:outline-none"
           @focus="open = true" @input="open = true" @blur="blur" @keydown="onInputKey" />
    <kbd class="pointer-events-none absolute end-2 top-1/2 -translate-y-1/2 max-md:hidden">⌘K</kbd>

    <div v-if="open && text.trim()" id="quick-search-results" role="listbox" :aria-label="t('shell.search.label')"
         class="absolute inset-x-0 top-[calc(100%+6px)] z-50 max-h-[420px] overflow-y-auto rounded-card border border-border bg-card p-1.5 shadow-lg">
      <template v-for="g in groups" :key="g.kind">
        <div class="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[.06em] text-muted-foreground rtl:tracking-normal">
          {{ t(`shell.search.group.${g.kind}`) }}
        </div>
        <button v-for="h in g.items" :id="`qs-${h.key}`" :key="h.key" type="button" role="option" :aria-selected="hits[active]?.key === h.key"
                class="flex w-full items-center gap-2.5 rounded-input px-2 py-1.5 text-start"
                :class="hits[active]?.key === h.key ? 'bg-primary/10' : 'hover:bg-muted'"
                @mousedown.prevent @click="go(h)" @mouseenter="active = hits.indexOf(h)">
          <span class="grid size-7 shrink-0 place-items-center rounded-input bg-muted text-muted-foreground"><Icon :name="h.icon" :size="15" /></span>
          <span class="grid min-w-0 flex-1 leading-tight">
            <span class="truncate text-[13px] font-medium">{{ h.label }}</span>
            <span v-if="h.detail" class="truncate text-[11.5px] text-muted-foreground">{{ h.detail }}</span>
          </span>
        </button>
      </template>
      <p v-if="!hits.length" class="m-0 px-2 py-2 text-[12.5px] text-muted-foreground">
        {{ loading ? t('shell.search.searching') : !long ? t('shell.search.typeMore') : t('shell.search.none') }}
      </p>
      <p v-if="long" class="m-0 border-t border-border px-2 pb-1 pt-2 text-[11.5px] text-muted-foreground">{{ t('shell.search.enterAll') }}</p>
    </div>
  </form>
</template>
